/**
 * Modal overlay chrome for `window.SalonLift.open(...)`.
 *
 * Vanilla DOM (no Preact) — the imperative API needs the overlay alive
 * before the App renders so this stays decoupled from the React tree.
 *
 * Creates two siblings in `document.body`:
 *   - backdrop  (light DOM, z-index 2147483640, click-to-close)
 *   - host      (light DOM, z-index 2147483641, attaches Shadow DOM)
 *
 * Inside the host's Shadow DOM:
 *   - <style> with the app CSS + overlay-specific CSS
 *   - <div class="sbw-overlay-card"> — the modal card
 *       - <button class="sbw-overlay-close"> — × button (focusable)
 *       - <div class="sbw-overlay-mount" data-sb-mount> — mountPoint
 *
 * Z-index ladder verified safe: Payment-step light-DOM portal at
 * 2147483646 (steps/Payment.tsx) floats above the modal card.
 *
 * Returns `{ mountPoint, destroy }`. Caller renders the App into
 * `mountPoint`; `destroy()` removes both DOM nodes, drops listeners,
 * restores body scroll lock + prior focus. Destroy is idempotent.
 */

interface CreateOverlayOpts {
  onClose: () => void;
  appStyles: string;
  /**
   * 'modal' = centered card with scale-up animation (default).
   * 'drawer' = right-edge anchored panel with slide-in-from-right.
   * Both share backdrop, focus trap, scroll lock, escape, close button.
   */
  style?: 'modal' | 'drawer';
}

interface OverlayHandle {
  mountPoint: HTMLElement;
  destroy: () => void;
}

const OVERLAY_STYLES = `
.sbw-overlay-card {
  pointer-events: auto;
  background: white;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  opacity: 0;
}
.sbw-overlay-card.sbw-overlay-card--open {
  opacity: 1;
}

/* Modal variant — centered card with scale-up. */
.sbw-overlay-card--modal {
  width: min(480px, 100vw);
  max-height: min(90vh, 800px);
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
  transform: scale(0.96) translateY(8px);
  transition: transform 200ms ease-out, opacity 200ms ease-out;
}
.sbw-overlay-card--modal.sbw-overlay-card--open {
  transform: scale(1) translateY(0);
}

/* Drawer variant — right-edge anchored, slides in from right.
   Viewport-unit cascade is vh → dvh so dvh-capable browsers stay
   anchored to the actual visible bottom as Android Chrome's URL bar
   collapses. Without dvh the inner runbar appeared to float above
   the real bottom of the viewport on Android (reported 2026-05-12). */
.sbw-overlay-card--drawer {
  width: min(480px, 100vw);
  height: 100vh;
  height: 100dvh;
  max-height: 100vh;
  max-height: 100dvh;
  border-radius: 16px 0 0 16px;
  box-shadow: -24px 0 64px rgba(0, 0, 0, 0.35);
  transform: translateX(32px);
  transition: transform 220ms ease-out, opacity 200ms ease-out;
}
.sbw-overlay-card--drawer.sbw-overlay-card--open {
  transform: translateX(0);
}

@media (max-width: 640px) {
  .sbw-overlay-card--modal,
  .sbw-overlay-card--drawer {
    width: 100vw;
    max-width: 100vw;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    border-radius: 0;
  }
}
.sbw-overlay-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  border: 0;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  cursor: pointer;
  font-size: 22px;
  line-height: 1;
  color: #111;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.sbw-overlay-close:hover {
  background: rgba(0, 0, 0, 0.12);
}
.sbw-overlay-close:focus-visible {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
}
/* Be You's header is dark, so the default near-black × is invisible. The
   variant class lives on the app root inside the mount point, so scope via
   :has() on the card. Other tenants (light headers) keep the dark ×. */
.sbw-overlay-card:has(.sbw-variant-be-you) .sbw-overlay-close {
  color: #fff;
  background: rgba(255, 255, 255, 0.14);
}
.sbw-overlay-card:has(.sbw-variant-be-you) .sbw-overlay-close:hover {
  background: rgba(255, 255, 255, 0.24);
}
.sbw-overlay-mount {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
}
@media (prefers-reduced-motion: reduce) {
  .sbw-overlay-card,
  .sbw-overlay-card--modal,
  .sbw-overlay-card--drawer,
  .sbw-overlay-card.sbw-overlay-card--open {
    transition: none !important;
    transform: none !important;
    opacity: 1 !important;
  }
}
`;

const BACKDROP_STYLES = [
  'position: fixed',
  'inset: 0',
  'background: rgba(15, 15, 20, 0.55)',
  'z-index: 2147483640',
  'opacity: 0',
  'transition: opacity 180ms ease-out',
  'pointer-events: auto',
].join('; ');

function hostStyles(style: 'modal' | 'drawer'): string {
  const align =
    style === 'drawer'
      ? 'align-items: stretch; justify-content: flex-end'
      : 'align-items: center; justify-content: center';
  return [
    'position: fixed',
    'inset: 0',
    'z-index: 2147483641',
    // pointer-events on host is none so clicks in the gap around the card
    // fall through to the backdrop (which closes). The card opts back in
    // via `pointer-events: auto` so its clicks don't bubble.
    'pointer-events: none',
    'display: flex',
    align,
  ].join('; ');
}

// `iframe` matters as much as the form controls here. The Boulevard and
// sb-vault card fields live inside an iframe with no tabindex attribute,
// so without it the trap cycled only between the close and back buttons
// and a keyboard user could never reach the card fields — unable to pay
// at all, in a modal they could not complete.
//
// Getting back OUT of a cross-origin frame stays the browser's job:
// keystrokes inside it don't bubble to this handler. Focus lands on
// whatever follows the iframe in the parent, which this handler does see.
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), iframe:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

export function createOverlay(opts: CreateOverlayOpts): OverlayHandle {
  const style = opts.style ?? 'modal';

  // Snapshot prior state so destroy() can restore it.
  const restoreFocus =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  const prevBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';

  // 1. Backdrop (light DOM, sibling of host)
  const backdrop = document.createElement('div');
  backdrop.setAttribute('data-sb-overlay-backdrop', '');
  backdrop.style.cssText = BACKDROP_STYLES;

  // 2. Host (light DOM, attaches Shadow DOM)
  const host = document.createElement('div');
  host.setAttribute('data-sb-widget', '');
  host.setAttribute('data-sb-widget-overlay', '');
  host.setAttribute('data-sb-overlay-style', style);
  host.style.cssText = hostStyles(style);

  const shadow = host.attachShadow({ mode: 'open' });

  // 3. Inject styles — app CSS first, then overlay-specific CSS so the
  // overlay can override if needed.
  const styleEl = document.createElement('style');
  styleEl.textContent = opts.appStyles + OVERLAY_STYLES;
  shadow.appendChild(styleEl);

  // 4. Build the card with close button + mount point. Variant class
  // drives whether it lays out as a centered modal or a right-anchored
  // drawer.
  const card = document.createElement('div');
  card.className = `sbw-overlay-card sbw-overlay-card--${style}`;
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-modal', 'true');

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'sbw-overlay-close';
  closeBtn.setAttribute('aria-label', 'Close booking');
  closeBtn.textContent = '×'; // ×

  const mountPoint = document.createElement('div');
  mountPoint.className = 'sbw-overlay-mount';
  mountPoint.setAttribute('data-sb-mount', '');

  card.appendChild(closeBtn);
  card.appendChild(mountPoint);
  shadow.appendChild(card);

  // 5. Append to body (backdrop first so it sits behind the host in DOM
  // order — z-index makes this redundant but keeps the source readable).
  document.body.appendChild(backdrop);
  document.body.appendChild(host);

  // 6. Trigger open animation on the next frame so the initial styles
  // get committed before the transition starts.
  requestAnimationFrame(() => {
    backdrop.style.opacity = '1';
    card.classList.add('sbw-overlay-card--open');
  });

  // 7. Land focus on the close button so keyboard users have a known
  // anchor inside the modal. The widget's first focusable becomes
  // reachable via Tab.
  closeBtn.focus({ preventScroll: true });

  // 8. Wire interactions
  const onBackdropClick = () => opts.onClose();
  const onCloseClick = () => opts.onClose();
  backdrop.addEventListener('click', onBackdropClick);
  closeBtn.addEventListener('click', onCloseClick);

  const onKeydown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      opts.onClose();
      return;
    }
    if (e.key !== 'Tab') return;

    // Focus trap. Query focusables fresh on each Tab so newly-rendered
    // step content (e.g. Service → Time) participates in cycling.
    const focusables = Array.from(
      shadow.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => el.offsetParent !== null || el === closeBtn);
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = shadow.activeElement as HTMLElement | null;

    if (e.shiftKey) {
      if (active === first || active === null) {
        e.preventDefault();
        last.focus();
      }
    } else {
      // active === null when focus has escaped the shadow root entirely
      // (e.g. user clicked back into the browser chrome). Pull it back to
      // the first focusable so Tab can never leak out of the modal.
      if (active === last || active === null) {
        e.preventDefault();
        first.focus();
      }
    }
  };
  window.addEventListener('keydown', onKeydown);

  let destroyed = false;
  function destroy() {
    if (destroyed) return;
    destroyed = true;
    window.removeEventListener('keydown', onKeydown);
    backdrop.removeEventListener('click', onBackdropClick);
    closeBtn.removeEventListener('click', onCloseClick);
    backdrop.remove();
    host.remove();
    document.body.style.overflow = prevBodyOverflow;
    if (restoreFocus && restoreFocus.isConnected) {
      restoreFocus.focus({ preventScroll: true });
    }
  }

  return { mountPoint, destroy };
}
